import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check, Gift, Loader2, Megaphone, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MARKDOWN_REMARK_PLUGINS } from "@/lib/markdown";
import { refreshBoideUser } from "@/hooks/use-boide-user";
import {
  BOIDE_AUTH_CHANGED_EVENT,
  announcementImageSrc,
  claimAnnouncementReward,
  dismissAnnouncement,
  getBoideToken,
  markAnnouncementSeen,
  type Announcement,
} from "@/lib/boide-api";
import { loadActiveAnnouncements } from "@/lib/bootstrap";

/**
 * Popup thông báo do admin tạo (/admin/announcements).
 *
 * Hai lựa chọn của người xem, KHÁC NHAU về nơi lưu — đó là cả điểm của tính năng:
 *  · "Tắt thông báo"      → chỉ đóng lượt này, lưu ở sessionStorage nên lần vào
 *                            web sau vẫn thấy lại cho tới khi popup hết hạn.
 *  · "Không hiện lại nữa" → gọi BE, lưu theo TÀI KHOẢN nên tắt hẳn ở mọi thiết bị.
 *                            Admin tắt được lựa chọn này (`allow_dismiss=false`)
 *                            cho thông báo bắt buộc phải đọc — khi đó chỉ còn nút
 *                            đầu, và BE cũng từ chối `/dismiss` (403), không chỉ
 *                            ẩn nút.
 *
 * Admin còn đặt được QUÀ (`reward_amount`): tiền vào thẳng Ví Bói, mỗi tài khoản
 * đúng một lần — cờ "đã nhận" nằm ở BE nên nhận trên app thì web cũng thấy đã nhận.
 *
 * ⚠️ Mount ở `__root` chứ không phải một trang cụ thể: popup phải chào người dùng
 * ở bất kỳ trang nào họ mở đầu tiên, mà điều hướng trong SPA không tạo mount mới.
 */

// Đóng bằng "Tắt thông báo" chỉ có hiệu lực trong phiên duyệt web hiện tại —
// sessionStorage tự hết khi đóng tab, đúng nghĩa "lần vào web sau hiện lại".
const CLOSED_KEY = "boide_announcement_closed";

function readClosedIds(): number[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(CLOSED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "number") : [];
  } catch {
    return [];
  }
}

function rememberClosed(id: number) {
  if (typeof window === "undefined") return;
  try {
    const next = Array.from(new Set([...readClosedIds(), id]));
    sessionStorage.setItem(CLOSED_KEY, JSON.stringify(next));
  } catch {
    /* sessionStorage bị chặn (chế độ riêng tư) — popup hiện lại là chấp nhận được */
  }
}

/**
 * Tiêu đề cũng nhận Markdown, nhưng CHỈ dạng inline: `p` phải ra `<span>` vì
 * `DialogTitle` đã là một `<h2>` — nhét `<p>` vào trong là HTML không hợp lệ,
 * trình duyệt tự đóng thẻ và bố cục vỡ trong im lặng. Cố ý không map heading /
 * danh sách: một cái `#` trong ô tiêu đề không nên đổi được cỡ chữ của popup.
 */
const TITLE_MD_COMPONENTS = {
  p: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-primary">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => <em className="italic">{children}</em>,
  del: ({ children }: { children?: React.ReactNode }) => (
    <span className="line-through opacity-70">{children}</span>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    <span className="font-mono text-[0.9em]">{children}</span>
  ),
  a: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
  // Xuống dòng / danh sách trong tiêu đề: giữ chữ, bỏ bố cục.
  ul: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
  ol: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
  li: ({ children }: { children?: React.ReactNode }) => <span>{children} </span>,
  h1: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
  h2: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
  h3: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
};

const money = (value: number) => `${Math.round(value).toLocaleString("vi-VN")}đ`;

export function AnnouncementPopup() {
  const [items, setItems] = useState<Announcement[]>([]);
  const [closedIds, setClosedIds] = useState<number[]>(() => readClosedIds());
  const [seenId, setSeenId] = useState<number>();
  const [claimingId, setClaimingId] = useState<number>();

  const load = useCallback(() => {
    if (!getBoideToken()) {
      setItems([]);
      return;
    }
    void loadActiveAnnouncements()
      .then((res) => setItems(res.items || []))
      .catch(() => setItems([]));
  }, []);

  // Nạp lúc mount + mỗi lần đăng nhập/đăng xuất (token đổi).
  useEffect(() => {
    load();
    window.addEventListener(BOIDE_AUTH_CHANGED_EVENT, load);
    return () => window.removeEventListener(BOIDE_AUTH_CHANGED_EVENT, load);
  }, [load]);

  // Popup ưu tiên cao nhất chưa bị đóng/tắt. Đóng cái này thì cái kế hiện ra.
  const current = useMemo(
    () => items.find((item) => !closedIds.includes(item.id)),
    [items, closedIds],
  );

  // Đếm lượt hiện — mỗi popup một lần trong vòng đời component này.
  useEffect(() => {
    if (!current || seenId === current.id) return;
    setSeenId(current.id);
    void markAnnouncementSeen(current.id);
  }, [current, seenId]);

  const close = useCallback((id: number) => {
    rememberClosed(id);
    setClosedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const dismissForever = useCallback(
    (id: number) => {
      // Ẩn ngay, không chờ mạng: kết quả BE không đổi được điều người xem vừa chọn.
      close(id);
      void dismissAnnouncement(id).catch(() => undefined);
    },
    [close],
  );

  /**
   * Nhận quà. Ngược với hai nút tắt ở trên: ở đây PHẢI chờ BE rồi mới đổi giao diện —
   * đây là tiền, báo "đã nhận" trước khi ví thật sự cộng là nói dối người dùng.
   */
  const claimReward = useCallback(async (id: number) => {
    setClaimingId(id);
    try {
      const result = await claimAnnouncementReward(id);
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, reward_claimed: true } : item)),
      );
      toast.success(`Đã nhận ${money(result.reward_amount)} vào Ví Bói`);
      // Số dư ở thanh điều hướng phải đổi ngay, nếu không người dùng tưởng quà chưa vào.
      void refreshBoideUser();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Không nhận được quà, thử lại nhé");
    } finally {
      setClaimingId(undefined);
    }
  }, []);

  if (!current) return null;

  const imageSrc = announcementImageSrc(current);
  const ctaUrl = current.cta_url?.trim();
  const ctaLabel = current.cta_label?.trim();
  const hasCta = Boolean(ctaUrl && ctaLabel);
  const isInternalCta = Boolean(ctaUrl?.startsWith("/"));
  const reward = Number(current.reward_amount || 0);
  // Quà CHƯA nhận chiếm chỗ nút chính: nó là lý do đáng bấm nhất trên popup này.
  // Nhận xong thì CTA của admin trở lại làm nút chính.
  const canClaim = reward > 0 && !current.reward_claimed;
  const isClaiming = claimingId === current.id;
  /** Nút chính KHÔNG phải nút đóng — lúc ấy "Tắt thông báo" mới cần là chữ nhỏ.
      Quà đã nhận mà không có CTA thì nút chính lại là "Đã hiểu", tức đã đóng được rồi. */
  const hasPrimaryAction = hasCta || canClaim;

  // Nút đóng của DialogContent không có nền, đặt trên ảnh banner là chìm mất —
  // nên tự vẽ nút có nền mờ.
  const closeButton = (
    <button
      type="button"
      onClick={() => close(current.id)}
      aria-label="Đóng thông báo"
      className="absolute right-3 top-3 z-10 rounded-full border border-gold-soft/40 bg-background/80 p-1.5 text-muted-foreground backdrop-blur transition hover:text-foreground"
    >
      <X className="h-4 w-4" />
    </button>
  );

  return (
    <Dialog open onOpenChange={(open) => !open && close(current.id)}>
      <DialogContent
        hideClose
        className="max-w-md overflow-hidden rounded-2xl border-gold-soft/40 p-0 shadow-gold sm:rounded-2xl"
      >
        {closeButton}

        {/* Banner tràn viền — có khoảng đệm quanh ảnh thì trông như ảnh dán vào. */}
        {imageSrc && (
          <img
            src={imageSrc}
            alt=""
            className="h-40 w-full object-cover sm:h-48"
            /* Ảnh hỏng thì giấu hẳn — khung ảnh vỡ giữa popup còn tệ hơn không ảnh. */
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}

        <div className="max-h-[60vh] overflow-y-auto overflow-x-hidden px-5 pt-5 sm:px-6">
          <DialogHeader className="min-w-0 space-y-2 text-left">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                <Megaphone className="h-3.5 w-3.5" />
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Thông báo
              </span>
            </div>
            <DialogTitle
              asChild
              className="pr-6 font-display text-xl leading-snug text-foreground [overflow-wrap:anywhere]"
            >
              <h2>
                <ReactMarkdown
                  remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                  components={TITLE_MD_COMPONENTS}
                >
                  {current.title}
                </ReactMarkdown>
              </h2>
            </DialogTitle>
            <DialogDescription className="sr-only">Thông báo từ Bói Đê</DialogDescription>
          </DialogHeader>

          {current.body_md?.trim() && (
            <div className="mt-3 min-w-0 text-sm leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
              <ReactMarkdown
                remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                components={{
                  p: ({ children }) => (
                    <p className="my-2.5 leading-relaxed first:mt-0 last:mb-0">{children}</p>
                  ),
                  /* Vàng thương hiệu. ⚠️ Nó chỉ còn tác dụng nhấn khi admin dùng
                     `**...**` TIẾT CHẾ — đậm gần hết đoạn thì cả đoạn hoá một khối
                     vàng và không còn gì nổi lên nữa. Muốn chữ đậm trung tính thì
                     đổi đúng dòng dưới thành `text-foreground`. */
                  strong: ({ children }) => (
                    <strong className="font-semibold text-primary">{children}</strong>
                  ),
                  em: ({ children }) => <em className="italic">{children}</em>,
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-primary underline underline-offset-2 [overflow-wrap:anywhere] hover:opacity-80"
                    >
                      {children}
                    </a>
                  ),
                  ul: ({ children }) => <ul className="my-2.5 space-y-1.5">{children}</ul>,
                  ol: ({ children }) => (
                    <ol className="my-2.5 list-decimal space-y-1.5 pl-5">{children}</ol>
                  ),
                  /* Dấu đầu dòng vàng nhỏ thay `list-disc`: bullet mặc định to và
                     xám, nằm giữa một popup vàng-kem trông như lỗi bố cục. */
                  li: ({ children }) => (
                    <li className="relative pl-4 leading-relaxed before:absolute before:left-0 before:top-[0.55em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-primary/60 before:content-['']">
                      {children}
                    </li>
                  ),
                }}
              >
                {current.body_md}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Vùng hành động: ĐÚNG MỘT nút chính. Hai lựa chọn tắt là chữ nhỏ mờ —
            ba nút full-width xếp chồng làm người xem phải đọc cả ba mới biết bấm gì. */}
        <div className="mt-4 border-t border-gold-soft/25 px-5 pb-5 pt-4 sm:px-6">
          {canClaim && (
            <Button
              className="mb-2 h-11 w-full bg-gold-gradient text-sm font-semibold text-primary-foreground shadow-gold hover:opacity-90"
              disabled={isClaiming}
              onClick={() => void claimReward(current.id)}
            >
              {isClaiming ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Gift className="mr-2 h-4 w-4" />
              )}
              {isClaiming ? "Đang nhận quà..." : `Nhận ${money(reward)} vào Ví Bói`}
            </Button>
          )}

          {/* Đã nhận thì vẫn nói rõ ra: giấu mất là người dùng tưởng quà bốc hơi. */}
          {reward > 0 && current.reward_claimed && (
            <div className="mb-2 flex items-center justify-center gap-2 rounded-xl border border-gold-soft/40 bg-primary/10 px-3 py-2.5 text-sm font-medium text-primary">
              <Check className="h-4 w-4" />
              Đã nhận {money(reward)} vào Ví Bói
            </div>
          )}

          {hasCta ? (
            <Button
              asChild
              variant={canClaim ? "outline" : undefined}
              className={
                canClaim
                  ? "h-11 w-full border-gold-soft/50 text-sm font-medium"
                  : "h-11 w-full bg-gold-gradient text-sm font-semibold text-primary-foreground shadow-gold hover:opacity-90"
              }
            >
              {isInternalCta ? (
                <Link to={ctaUrl!} onClick={() => close(current.id)}>
                  {ctaLabel}
                </Link>
              ) : (
                <a
                  href={ctaUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => close(current.id)}
                >
                  {ctaLabel}
                </a>
              )}
            </Button>
          ) : canClaim ? null : (
            /* Không có CTA thì popup phải có một nút chính nào đó, nếu không cả
               vùng hành động chỉ còn hai dòng chữ mờ. */
            <Button
              variant="outline"
              className="h-11 w-full border-gold-soft/50 text-sm font-medium"
              onClick={() => close(current.id)}
            >
              Đã hiểu
            </Button>
          )}

          <div className="mt-3 flex items-center justify-center gap-1 text-xs">
            {hasPrimaryAction && (
              <button
                type="button"
                onClick={() => close(current.id)}
                className="rounded-full px-2.5 py-1 text-muted-foreground transition hover:text-foreground"
              >
                Tắt thông báo
              </button>
            )}
            {hasPrimaryAction && current.allow_dismiss && (
              <span aria-hidden className="text-muted-foreground/40">
                ·
              </span>
            )}
            {current.allow_dismiss && (
              <button
                type="button"
                onClick={() => dismissForever(current.id)}
                className="rounded-full px-2.5 py-1 text-muted-foreground/80 transition hover:text-foreground"
              >
                Không hiện lại nữa
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
